import type { Maybe } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { currentUser, isImpersonating } from '@adornis/users/client/currentUser.js';
import { logout } from '@adornis/users/client/loginFunctions.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { projectConfig } from '../../config.js';
import { ChancenPermission } from '../../db/permissions.js';
import { ChancenwerkUser } from '../../master-data-management/users/entities/ChancenwerkUser.js';
import { ChromiumDialog } from '../design/components/cr-dialog.js';

/**
 * @customElement chancen-component-logout-button
 */
@customElement('chancen-component-logout-button')
export class ChancenComponentLogoutButton extends ChemistryLitElement {
  @state() private readonly _currentUser = new RXController(this, currentUser<ChancenwerkUser>());

  @property({ attribute: false }) public labelOverride: Maybe<string> = null;
  @property({ attribute: false }) public hrefOverride: Maybe<string> = null;

  override styles() {
    return [
      ...super.styles(),
      {
        ':host': {
          flex: this._currentUser.value ? '3' : '0',
        },
      },
    ];
  }

  private _buttonText(): string {
    if (this.labelOverride) return this.labelOverride;

    if (!this._currentUser.value) return '';

    if (isImpersonating.value) {
      return 'Impersonation beenden';
    }
    if (
      this._currentUser.value.hasPermission(ChancenPermission.netz.LOGIN_NETZ, ANY_CONTEXT) &&
      !this._currentServiceIsAuthProvider()
    ) {
      return 'Zum CHANCENnetz';
    }
    return 'Abmelden';
  }

  private _isVidis() {
    return this._currentUser.value?.email.includes('vidisdummy');
  }

  override render() {
    if (this._currentUser.value) return this._logoutButton();
    return nothing;
  }

  private _logoutButton(): Renderable {
    return html`
      <cr-flex
        center
        crossaxis-center
        ${css({
          color: 'white',
          border: '1px solid white',
          cursor: 'pointer',
          borderRadius: this.sizes.borderRadius,
        })}
        @click=${async () => {
          console.log(this.hrefOverride);
          if (this.hrefOverride) {
            goTo(this.hrefOverride);
            return;
          }

          if (isImpersonating.value) {
            await ChancenwerkUser.stopImpersonation();
            return;
          }
          await this._logoutOrRouteToNetz();
        }}
        padding="sm"
      >
        <cr-icon>logout</cr-icon>
        <cr-tooltip>
          <cr-text> ${this._buttonText()} </cr-text>
        </cr-tooltip>
      </cr-flex>
    `;
  }

  private async _logoutOrRouteToNetz() {
    if (this._currentServiceIsAuthProvider()) {
      if (
        !(await ChromiumDialog.confirm('Du bist im Begriff dich abzumelden. Forfahren?', {
          highlightDecline: true,
          acceptText: 'Jetzt Abmelden!',
          declineText: 'Abbrechen',
        }))
      )
        return;
    }

    // Setting the page to logout state, so the automatic silent login isnt triggered
    routingState.searchParams.set('logout', '1');
    await logout();

    if (this._currentServiceIsAuthProvider()) {
      goTo(projectConfig.get('NETZ_URL'), { target: '_parent' });
      return;
    }

    const params = new URLSearchParams({
      client_id: projectConfig.get('OAUTH_CLIENT_ID'),
    }).toString();

    if (this._isVidis()) {
      if (baseConfig.get('DEPLOYMENT') === 'staging') {
        goTo(
          `https://aai-test.vidis.schule/auth/realms/vidis/protocol/openid-connect/logout?post_logout_redirect_uri=${
            projectConfig.get('NETZ_URL') + 'authentication/logout?' + params
          }`,
        );
      } else {
        goTo(
          `https://aai.vidis.schule/auth/realms/vidis/protocol/openid-connect/logout?post_logout_redirect_uri=${
            projectConfig.get('NETZ_URL') + 'authentication/logout?' + params
          }`,
        );
      }
      return;
    }

    goTo(projectConfig.get('NETZ_URL') + 'authentication/logout?' + params, { target: '_parent' });
  }

  private _currentServiceIsAuthProvider() {
    return window.location.toString().includes(projectConfig.get('NETZ_URL'));
  }
}
